// ============================================
// FILE: apiSetup/axiosInterceptor.ts (UPDATED)
// ============================================
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from '../../apiSetup/apiConfig';

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

// Create axios instance with dynamic base URL
export const apiClient = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update base URL dynamically before each request
apiClient.interceptors.request.use(
  (config) => {
    // Always get fresh URL from localStorage
    config.baseURL = getApiBaseUrl();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return true;
    
    const payload = JSON.parse(atob(tokenParts[1]));
    if (!payload.exp) return true;
    
    // Add 10 second buffer to refresh before actual expiry
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const isExpired = currentTime >= (expiryTime - 10000);
    
    if (isExpired) {
      console.log('⏰ Token expired at:', new Date(expiryTime).toLocaleString());
      console.log('⏰ Current time:', new Date(currentTime).toLocaleString());
    }
    
    return isExpired;
  } catch (e) {
    console.error('❌ Failed to decode token:', e);
    return true;
  }
};

// Helper function to refresh token
const refreshAccessToken = async (): Promise<string> => {
  const refreshTokenValue = Cookies.get('refreshToken');
  
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  console.log('🔄 Refreshing access token...');
  const baseUrl = getApiBaseUrl();
  console.log('🔄 API URL:', `${baseUrl}api/refresh`);
  
  try {
    const response = await axios.post(`${baseUrl}api/refresh`, {
      refresh_token: refreshTokenValue
    });
    
    console.log('📦 Refresh response:', response.data);
    
    // Handle response structure from your API
    const { status, message, token, refresh_token } = response.data;
    
    // Check if API returned error status
    if (status === 'error') {
      console.error('❌ API returned error:', message);
      throw new Error(message || 'Token refresh failed');
    }
    
    // Validate tokens exist
    if (!token || !refresh_token) {
      console.error('❌ Missing tokens in response:', {
        hasToken: !!token,
        hasRefreshToken: !!refresh_token,
        status,
        message,
        fullResponse: response.data
      });
      throw new Error('Invalid refresh response: missing tokens');
    }
    
    // Validate token format
    if (typeof token !== 'string' || typeof refresh_token !== 'string') {
      console.error('❌ Invalid token format');
      throw new Error('Invalid refresh response: invalid token format');
    }
    
    // Validate tokens are not empty
    if (token.trim() === '' || refresh_token.trim() === '') {
      console.error('❌ Empty token values');
      throw new Error('Invalid refresh response: empty tokens');
    }
    
    console.log('✅ Token refreshed successfully');
    Cookies.set('accessToken', token, COOKIE_OPTIONS);
    Cookies.set('refreshToken', refresh_token, COOKIE_OPTIONS);
    
    return token;
  } catch (error: any) {
    console.error('❌ Token refresh error:', error);
    
    // Handle specific error responses
    if (error.response?.data?.status === 'error') {
      throw new Error(error.response.data.message || 'Token refresh failed');
    }
    
    throw error;
  }
};

// Request Interceptor - Check token expiry BEFORE sending request
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const accessToken = Cookies.get('accessToken');
    
    console.log('🔑 Token from cookies:', accessToken ? 'EXISTS' : 'MISSING');
    
    if (accessToken) {
      console.log('🔑 Token preview:', accessToken.substring(0, 20) + '...');
      
      // 🔥 CHECK IF TOKEN IS EXPIRED BEFORE SENDING REQUEST
      if (isTokenExpired(accessToken)) {
        console.log('⏰ Token is expired, refreshing before request...');
        
        // If already refreshing, wait for it
        if (isRefreshing) {
          console.log('⏳ Already refreshing, waiting...');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            if (config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
          });
        }
        
        isRefreshing = true;
        
        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          
          if (config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          console.log('✅ Using refreshed token for request');
        } catch (error) {
          console.error('❌ Token refresh failed, redirecting to login');
          processQueue(error as AxiosError, null);
          Cookies.remove('accessToken', COOKIE_OPTIONS);
          Cookies.remove('refreshToken', COOKIE_OPTIONS);
          window.location.href = '/login';
          throw error;
        } finally {
          isRefreshing = false;
        }
      } else {
        // Token is valid, use it
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          console.log('✅ Using valid token');
        }
      }
    }
    
    // Handle FormData (file uploads)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('📎 FormData detected, letting browser set Content-Type');
    } else if (config.headers && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor - Handle 401 errors as fallback
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ Response received:', response.config.url, response.status);
    
    // 🔥 CHECK FOR TOKEN ERROR IN 200 RESPONSES
    const data: any = response.data;
    
    // Check if response indicates token error even with 200 status
    if (data?.status === 'error') {
      const errorMsg = data.message || '';
      
      // Token-related error messages
      if (
        errorMsg.toLowerCase().includes('token') ||
        errorMsg.toLowerCase().includes('expired') ||
        errorMsg.toLowerCase().includes('invalid') ||
        errorMsg.toLowerCase().includes('unauthorized')
      ) {
        console.error('❌ Token error in response despite 200 status:', errorMsg);
        const error: any = new Error(errorMsg);
        error.response = response;
        error.response.status = 401; // Convert to 401
        throw error;
      }
    }
    
    return response;
  },
  async (error: AxiosError) => {
    console.error('❌ Response error:', error.response?.status, error.config?.url);
    console.error('❌ Error data:', error.response?.data);
    
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors as fallback (in case token wasn't checked in request interceptor)
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 Got 401, attempting token refresh...');
      
      if (isRefreshing) {
        console.log('⏳ Already refreshing, queueing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        console.error('❌ Token refresh failed:', refreshError);
        processQueue(refreshError as AxiosError, null);
        Cookies.remove('accessToken', COOKIE_OPTIONS);
        Cookies.remove('refreshToken', COOKIE_OPTIONS);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;